/**
 * Background watcher that polls a recording's transcription status until
 * it reaches a terminal state (done/failed). Separate from the recording
 * lifecycle so the conference view can fire-and-forget after upload.
 */
import config from "../config";
import { abortableDelay } from "./abortableDelay";
import { fetchRecording, RecordingMeta } from "./recordingsApi";

const DEFAULT_POLL_MS = 3000;
const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes hard cap

export interface WatchResult {
  status: "done" | "failed" | "timeout" | "missing";
  recording: RecordingMeta | null;
}

export interface WatchOptions {
  pollIntervalMs?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
}

export async function waitForTranscription(
  recordingId: string,
  opts: WatchOptions = {},
): Promise<WatchResult> {
  const pollInterval = opts.pollIntervalMs ?? DEFAULT_POLL_MS;
  const timeout = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const deadline = Date.now() + timeout;
  let consecutiveErrors = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (opts.signal?.aborted) return { status: "timeout", recording: null };
    if (Date.now() > deadline) return { status: "timeout", recording: null };

    try {
      const rec = await fetchRecording(recordingId);
      consecutiveErrors = 0;
      if (!rec) return { status: "missing", recording: null };
      const t = rec.transcription;
      if (!t) return { status: "missing", recording: rec };
      if (t.status === "done") return { status: "done", recording: rec };
      if (t.status === "failed") return { status: "failed", recording: rec };
    } catch {
      // Transient GraphQL error — back off a bit, then keep polling.
      consecutiveErrors = Math.min(consecutiveErrors + 1, 5);
    }

    try {
      await abortableDelay(pollInterval * (1 + consecutiveErrors), opts.signal);
    } catch {
      return { status: "timeout", recording: null };
    }
  }
}

/**
 * Build an absolute URL for the transcript download endpoint (txt/srt).
 * Suitable for embedding in a chat system message — the recipient opens
 * it in their browser, SESSIONID cookie auth kicks in.
 */
export function buildTranscriptDownloadUrl(
  recordingId: string,
  format: "txt" | "srt" = "txt",
): string {
  const apiUrl = config.apiUrl || "/graphql";
  let base: string;
  if (apiUrl.startsWith("/")) {
    base = window.location.origin;
  } else {
    base = apiUrl.replace(/\/graphql\/?$/, "").replace(/\/$/, "");
  }
  const safeId = String(recordingId).replace(/[^a-zA-Z0-9_-]/g, "");
  return `${base}/recordings/${safeId}/transcript.${format}`;
}
